import waRequest from './wa-request';

export const apiBase = '/reports/v1';

/**
 * Initiate authenticated GET ${apiBase}/dashboards/${reportId}
 *
 * @param reportId
 * @param includeDashboardUrl
 * @param locale
 * @param signal
 * @returns {Promise}
 */
function getReport({pathParams: {reportId}, params: {includeDashboardUrl, locale} = {}, signal}) {
  const url = `${apiBase}/reports/${reportId}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    signal,
    params: {
      includeDashboardUrl,
      locale,
    },
  });
}

/**
 * Get details about a particular report.
 *
 * Includes support for generating a URL for the dashboard associated with the report, which can be used in embedding.
 *
 * @param {Object} config - The configuration object for the request.
 * @param {Object} config.params - The parameters for the request.
 * @param {string} [config.params.locale='en'] - Locale in ISO 639-1 format in conjunction with ISO 15924, for example zh-Hans. Used as a query parameter to indicate the client wants the returned content translated in the specified language. Note that not all fields on a response support translation and that only specific languages are supported. Defaults to 'en' if not provided or translation not found.
 * @returns {Promise} - A promise that resolves with the response of the authenticated request.
 */
function getReports(config) {
  const {params, signal} = config;

  const url = `${apiBase}/reports`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params, signal});
}

/**
 * Initiate authenticated GET ${apiBase}/reports/${reportId}/schedules
 *
 * @returns {Promise}
 */
function getReportSchedules({pathParams: {reportId}, params: {recipientTypes, organizationId} = {}, signal}) {
  const url = `${apiBase}/reports/${reportId}/schedules`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {recipientTypes, organizationId},
    signal,
  });
}

function getReportSchedule({pathParams: {reportId, scheduleId}, signal}) {
  const url = `${apiBase}/reports/${reportId}/schedules/${scheduleId}`;

  return waRequest.requestAuthenticated({url: url, method: 'get', signal});
}

function addReportSchedule({pathParams: {reportId}, data, signal}) {
  const url = `${apiBase}/reports/${reportId}/schedules`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data: {
      scheduleName: data.scheduleName,
      frequencyOfDelivery: data.frequencyOfDelivery,
      recipientType: data.recipientType,
      organizationId: data.organizationId,
      deliveryMode: data.deliveryMode,
      schedule: {
        startDate: data.startDate,
        endDate: data.endDate,
        runAtTime: data.runAtTime,
        frequencyType: data.frequencyType,
        //// daily
        repeatDailyInterval: data.repeatDailyInterval,
        //// weekly
        daysOfWeek: data.daysOfWeek,
        //// monthly
        repeatMonthlyInterval: data.repeatMonthlyInterval,
        daysOfMonth: data.daysOfMonth,
      },
      userScheduleFilterValues: data.userScheduleFilterValues,
      toOrganizationRecipientIds: data.toOrganizationRecipientIds,
      sftpConfigId: data.sftpConfigId,
    },
    signal,
  });
}

function updateReportSchedule({pathParams: {reportId, scheduleId}, data, signal}) {
  const url = `${apiBase}/reports/${reportId}/schedules/${scheduleId}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'put',
    data: {
      scheduleName: data.scheduleName,
      frequencyOfDelivery: data.frequencyOfDelivery,
      recipientType: data.recipientType,
      organizationId: data.organizationId,
      deliveryMode: data.deliveryMode,
      schedule: {
        startDate: data.startDate,
        endDate: data.endDate,
        runAtTime: data.runAtTime,
        frequencyType: data.frequencyType,
        //// daily
        repeatDailyInterval: data.repeatDailyInterval,
        //// weekly
        daysOfWeek: data.daysOfWeek,
        //// monthly
        repeatMonthlyInterval: data.repeatMonthlyInterval,
        daysOfMonth: data.daysOfMonth,
      },
      userScheduleFilterValues: data.userScheduleFilterValues,
      toOrganizationRecipientIds: data.toOrganizationRecipientIds,
      sftpConfigId: data.sftpConfigId,
    },
    signal,
  });
}

function deleteReportSchedule({pathParams}) {
  const url = `${apiBase}/reports/${pathParams.reportId}/schedules/${pathParams.scheduleId}`;

  return waRequest.requestAuthenticated({url: url, method: 'delete'});
}

function getReportsSchedules({params = {}, signal}) {
  const url = `${apiBase}/reports/schedules`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params, signal});
}

export default {
  getReport,
  getReports,
  getReportSchedules,
  getReportSchedule,
  addReportSchedule,
  updateReportSchedule,
  deleteReportSchedule,
  getReportsSchedules,
};
